import React, { useContext, useEffect, useRef, useState } from "react";
import { connect, Provider as ReduxProvider } from "react-redux";
import { ConfigProvider } from "antd";
import { getStore } from "./Store";
import Layout from "./Layout";
import Selectors from "./Selectors";
import ActionCreator from "./ActionCreator";
import * as Cart from "./Contexts/CartContext";
import ErrorBoundary from "./ErrorBoundary";
import "antd/dist/reset.css";
import ActivityIndicator from "./Components/ActivityIndicator";
import * as AppContext from "./AppContext";
import constants from "../src/constants";

if (!constants.debug || typeof window === "undefined") {
  console.log = () => 0;
  console.group = () => 0;
  console.groupEnd = () => 0;
}

export function PageContainer(props) {
  const { pageContext, children, ...extraProps } = props;
  return (
    <ConfigProvider
      theme={{
        hashed: false,
        token: {
          fontFamily: "-apple-system, Noto Sans TC, sans-serif",
          colorPrimary: "#3c3c3c",
          colorPrimaryHover: "#4a4a4a",
        },
        components: {
          Button: {
            borderRadius: 2,
            border: "1px solid transparent",
            primaryShadow: "0 2px 0 rgba(0,0,0,.045)",
          },
          Radio: {
            fontFamily: "Roboto, Noto Sans TC",
          },
          Table: {
            headerBg: "#505050",
            headerColor: "#fff",
            headerBorderRadius: 2,
            headerSplitColor: "none",
            padding: 10,
          },
          Input: {
            borderRadius: 2,
          },
          Modal: {
            contentPadding: 0,
          },
        },
      }}
    >
      <WithPageContext>
        <ErrorBoundary>
          <Layout {...extraProps} seo={pageContext.seo}>
            {props.children}
          </Layout>
        </ErrorBoundary>
      </WithPageContext>
    </ConfigProvider>
  );
}

function WithPageContext({ appActions, children, profile }) {
  const app = useContext(AppContext.Context);
  const [autoLoginDone, setAutoLoginDone] = useState(false);
  useEffect(() => {
    // 避免重複執行 autoLogin
    if (autoLoginDone) return;

    const performAutoLogin = async () => {
      try {
        // 檢查兩個 store 系統的狀態
        const reduxProfile = profile; // Redux store 的 profile
        const appProfile = app.state.profile; // AppContext 的 profile
        const hasToken =
          typeof window !== "undefined"
            ? window.localStorage.getItem("token")
            : null;

        console.log("AutoLogin Debug:", {
          reduxProfile: !!reduxProfile,
          appProfile: !!appProfile,
          hasToken: !!hasToken,
          pathname:
            typeof window !== "undefined" ? window.location.pathname : "SSR",
        });

        // 如果兩個 store 都沒有 profile，但有 token，則執行 autoLogin
        if (!reduxProfile && !appProfile && hasToken) {
          console.log("執行完整 autoLogin 流程");

          // 使用 Promise.allSettled 確保即使部分失敗也不會中斷整個流程
          const results = await Promise.allSettled([
            app.actions.autoLogin(),
            appActions.autoLogin({ setToken: app.actions.setToken }),
            Cart.Actions.fetchCart(),
          ]);

          // 檢查是否有任何操作失敗
          results.forEach((result, index) => {
            if (result.status === "rejected") {
              console.warn(`AutoLogin step ${index} failed:`, result.reason);
            }
          });
        }
        // 如果只有 AppContext 有 profile，但 Redux 沒有，則同步 Redux
        else if (!reduxProfile && appProfile) {
          console.log("同步 Redux store");
          await appActions
            .autoLogin({ setToken: app.actions.setToken })
            .catch((error) => {
              console.warn("Redux sync failed:", error);
            });
        }
        // 如果只有 Redux 有 profile，但 AppContext 沒有，則同步 AppContext
        else if (reduxProfile && !appProfile) {
          console.log("同步 AppContext store");
          await app.actions.autoLogin().catch((error) => {
            console.warn("AppContext sync failed:", error);
          });
        }
        // 如果兩個都有 profile，確保 Redux 的 autoLoginDone 狀態正確
        else if (reduxProfile && appProfile) {
          console.log("兩個 store 都有 profile，觸發狀態同步");
          // 不需要 await，只是觸發狀態更新
          appActions
            .autoLogin({ setToken: app.actions.setToken })
            .catch((error) => {
              console.warn("State sync failed (non-critical):", error);
            });
        }
        // 如果沒有 token，也要設置完成狀態，避免無限等待
        else if (!hasToken) {
          console.log("沒有 token，設置 autoLoginDone 狀態");
          await appActions
            .autoLogin({ setToken: app.actions.setToken })
            .catch((error) => {
              console.warn("No token autoLogin failed:", error);
            });
        }
      } catch (error) {
        // 確保任何未預期的錯誤都被捕獲
        console.error("AutoLogin unexpected error:", error);
      } finally {
        // 無論如何都要設置完成狀態
        setAutoLoginDone(true);
        console.log("AutoLogin 流程完成");
      }
    };

    // 使用 setTimeout 確保不會阻塞初始渲染
    const timeoutId = setTimeout(performAutoLogin, 0);

    // 清理函數
    return () => {
      clearTimeout(timeoutId);
    };
  }, [profile, app.state.profile, appActions, app.actions]); // 完整的依賴列表

  return children;
}

WithPageContext = connect(
  (state, ownProps) => ({
    lang: Selectors.getLang(state),
    profile: Selectors.getLoginUser(state),
  }),
  ActionCreator
)(WithPageContext);

export function AppRoot(props) {
  // const store = getStore(props.pageContext.initialState)
  const store = useRef(getStore({}));

  return (
    <ReduxProvider store={store.current}>
      <AppContext.Provider {...props}>
        <Cart.Provider>
          {props.children}
          <ActivityIndicator />
        </Cart.Provider>
      </AppContext.Provider>
    </ReduxProvider>
  );
}
